import { fetchTemplateShippingProfiles } from "@services/template";
import { initAppData } from "@utils/initAppData";
import { getReviewsKey } from "stores/useStore";
// import { RootState } from "@stores/store";
// import { useSelector } from "react-redux";
import printy6ImageLoader from "@lib/printy6ImageLoader";
import TemplateDetail from "containers/templateDetail";
import { useTemplate } from "containers/templateDetail/store";
import Layout from "layout/landingPage";
import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import { BreadcrumbJsonLd, NextSeo, ProductJsonLd } from "next-seo";
import { useSelector } from "react-redux";
import { RootState } from "stores/store";
import { SWRConfig } from "swr";
import Error from "../404";

type Props = {
  template: any;
  shippingProfiles: any[];
};

export default function Design({ shippingProfiles }: Props) {
  const template = useTemplate();
  const app = useSelector((store: RootState) => store.app);

  if (!template || !template.id) {
    return <Error statusCode={404} />;
  }

  const seoAttr = getSeoAttr(template);
  const productJsonAttr = getProductJsonAttr(template);
  const breadcrumbJsonAttr = getbreadcrumbJsonAttr(app, template);

  return (
    <Layout>
      <SWRConfig
        value={{
          fallback: {
            [getReviewsKey(template.id, 1)]: template.reviews,
            [`/templates/${template.id}/shippingProfiles`]: shippingProfiles,
          },
        }}
      >
        <NextSeo {...seoAttr} />
        <BreadcrumbJsonLd {...breadcrumbJsonAttr} />
        <ProductJsonLd {...productJsonAttr} />
        <TemplateDetail />
      </SWRConfig>
    </Layout>
  );
}

Design.getInitialProps = async (ctx) => {
  const isServer = !!ctx.req;

  await initAppData(ctx);

  let { slugs } = ctx.query;
  let id,
    title = null;

  if (slugs.length === 1) {
    id = slugs[0];
  } else if (slugs.length === 2) {
    if (!Number.isInteger(parseInt(slugs[0]))) {
      return {
        template: null,
      };
    }
    id = slugs[0];
    title = slugs[1];
  } else {
    return {
      template: null,
    };
  }

  await ctx.reduxStore.execSagaTasks(isServer, (dispatch) => {
    dispatch({
      type: "EFFECT_TEMPLATE_LOAD",
      payload: { [title ? "id" : "slug"]: id },
      options: {
        headers:
          ctx.req && ctx.req.headers.cookie
            ? { cookie: ctx.req.headers.cookie }
            : undefined,
      },
    });
  });

  const state = ctx.reduxStore.getState();

  const template = state.template.template;

  if (!template) {
    return {
      template: null,
    };
  }

  if (title && template.slug) {
    ctx.res.writeHead(301, {
      Location: `/design/${template.slug}`,
    });
    return ctx.res.end();
  }

  const shippingProfiles = template
    ? await fetchTemplateShippingProfiles({ id: template.id })
    : null;

  return {
    shippingProfiles,
    // device: ctx?.req?.device.type.toUpperCase() || null,
  };
};

const getSeoAttr = (template) => {
  const title = `${template.title} | Printy6`;
  const url = `https://www.printy6.com/design/${template.slug}`;

  let images = template.images.filter(
    (itme) => itme.color_id === null || itme.color_id === 6
  );

  if (!images || images.length < 1) {
    images = template.images;
  }

  const openGraphImages = images.map((item) => {
    return {
      url: printy6ImageLoader({
        src: item.src,
        quality: 1000,
      }),
      width: 1000,
      height: 1000,
    };
  });

  return Object.assign(
    {
      title,
      canonical: url,
      openGraph: Object.assign(
        {
          type: "website",
          url,
          title,
          images: openGraphImages,
        },
        template.meta_description
          ? {
              description: template.meta_description,
            }
          : {}
      ),
    },
    template.meta_description
      ? {
          description: template.meta_description,
        }
      : {}
  );
};

const getProductJsonAttr = (template) => {
  const images = template.images.map((item) =>
    printy6ImageLoader({ src: item.src, quality: 1000 })
  );

  const reviews =
    template.reviews?.reviews?.map((item) => {
      return {
        author: item.name,
        datePublished: item.created_at,
        reviewBody: item.content,
        reviewRating: {
          ratingValue: item.score.toFixed(1),
          bestRating: "5.0",
          worstRating: "1.0",
        },
      };
    }) || [];

  const lowPrice = minBy(template.variants, (o: any) => o.cost).cost.toFixed(2);

  const highPrice = maxBy(template.variants, (o: any) => o.cost).cost.toFixed(
    2
  );

  return Object.assign(
    {
      productName: template.title,
      description: template.meta_description || "",
      images,
      brand: "Printy6",
      manufacturerName: "Printy6",
      manufacturerLogo: "https://www.printy6.com/logo.svg",
      reviews,
      offers: Object.assign(
        {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          offerCount: "99999",
          // priceValidUntil,
          url: `https://www.printy6.com/design/${template.slug}`,
          itemCondition: "https://schema.org/NewCondition",
          availability: "https://schema.org/InStock",
        },
        lowPrice === highPrice
          ? {
              price: lowPrice,
            }
          : {
              lowPrice,
              highPrice,
            }
      ),
      sku: template.template_no,
    },
    // 假如 评论数大于 0,增加评论
    template?.review?.quantity > 0
      ? {
          aggregateRating: {
            ratingValue: template?.review?.score || "5",
            reviewCount: template?.review?.quantity || "0",
          },
        }
      : {}
  );
};

const getbreadcrumbJsonAttr = (app, template) => {
  const arr = [
    {
      position: 1,
      name: "Printy6",
      item: "https://www.printy6.com",
    },
    {
      position: 2,
      name: template.category.name,
      item: `https://www.printy6.com/custom/${template.category.key}`,
    },
    {
      position: 3,
      name: template.title,
      item: `https://www.printy6.com/design/${template.slug}`,
    },
  ];

  return {
    itemListElements: arr,
  };
};
